import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import React from 'react';
import moment from 'moment';
import I18n from 'i18n-js';
import { RouteComponentProps } from 'react-router';

import CMSListingsCollection from '@reverbdotcom/commons/src/cms/components/cms_listings_collection';
import URLHelpers, { MarketplacePathContext } from '../../shared/url_helpers';
import { CoreShopContainer, ShopContainerFragment } from '../../../schemas/rql';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

import { ShopHeader, ShopHeaderFragment } from './shop_header';
import ShopMetadata, { ShopMetadataFragment } from './shop_metadata';
import ShopTabs, { ShopTabsFragment } from './tabs';
import { IUserContext, withUserContext } from '@reverbdotcom/commons';
import { FavoriteShopCTAFragment } from '../../../../src/components/favorites/favorite_shop_cta';
import { EVERYWHERE_CODE } from '@reverbdotcom/commons/src/constants';

type RouteProps = RouteComponentProps<MarketplacePathContext, {}>;
type Props = ChildProps<IUserContext & RouteProps, CoreShopContainer.Query, CoreShopContainer.Variables>;

export const ShopContainerFragmentData = gql`
  fragment ShopContainerFragment on Shop {
    _id
    id
    showSold
    slug
    freeDomesticShippingRate {
      regionCode
      freeShippingThreshold {
        display
      }
    }
    sales {
      slug
      name
      endsAt {
        seconds
      }
      discountValue
      buyerIneligibilityReason
    }
  }
`;

export const ShopBuyerCouponsFragment = gql`
   fragment ShopBuyerCouponsFragment on Shop {
     _id
     buyerCoupons {
       _id
       __typename
       code
       discountValue
       descriptionSummary
       restrictionsSummary
       expiresAt
       status
       couponHeader
     }
   }`
;

function getSubtitle(s: ShopContainerFragment.Sales) {
  const date = moment.unix(s.endsAt.seconds).format('MMMM D');
  const { discountValue } = s;
  return I18n.t('discovery.shop.saleRow.subtitle', { date, discountValue });
}

export class ShopContainer extends React.Component<Props, null> {
  renderSaleRows() {
    if (!this.props.data.shop || !this.props.data.shop.sales) return [];
    return this.props.data.shop.sales.map((sale) => {
      if (sale.buyerIneligibilityReason) { return null; }

      return (
        <CMSListingsCollection
          key={sale.slug}
          title={sale.name}
          saleSlug={sale.slug}
          ctaTargetHref={URLHelpers.salePath(sale.slug)}
          subtitle={getSubtitle(sale)}
        />
      );
    });
  }

  render() {
    const shouldShowSoldListingsCount = this.props.data.shop?.showSold;
    const shopTabsListingCount = shouldShowSoldListingsCount ? this.props.data.shopListingsAll?.total : this.props.data.shopListingsLive?.total;
    const initialFavoriteId = this.props.data.findFavorite?.favorite?.id;
    const buyerCoupon = this.props.data.shop?.buyerCoupons?.[0];
    return (
      <div>
        <ShopMetadata shop={this.props.data.shop} />
        <ShopHeader
          shop={this.props.data.shop}
          initialFavoriteId={initialFavoriteId}
          buyerCoupon={buyerCoupon}
        />
        {this.renderSaleRows()}
        <div className="scaling-mb-6">
          <ShopTabs shop={this.props.data.shop} listingCount={shopTabsListingCount} />
        </div>
        {this.props.children}
      </div>
    );
  }
}

const connect = withGraphql<
  Props,
  CoreShopContainer.Query,
  CoreShopContainer.Variables
>(
  gql`
    query Core_Shop_Container(
      $shop: String
      $loggedOut: Boolean!
    ) {
      shop(input: { slug: $shop }) {
        _id
        ... ShopContainerFragment
        ... ShopHeaderFragment
        ... ShopMetadataFragment
        ... ShopTabsFragment
        ... FavoriteShopCTAData
        ... ShopBuyerCouponsFragment
      }
      shopListingsLive: listingsSearch(input: { shopSlug: $shop }) {
        total
      }
      shopListingsAll: listingsSearch(input: { shopSlug: $shop, showSold: true }) {
        total
      }
      findFavorite(
        input: {
          shopSlug: $shop
        }
      ) @skip(if: $loggedOut) {
        favorite {
          id
        }
      }
    }
    ${ShopBuyerCouponsFragment}
    ${ShopContainerFragmentData}
    ${ShopHeaderFragment}
    ${ShopTabsFragment}
    ${ShopMetadataFragment}
    ${FavoriteShopCTAFragment}
  `,
  {
    options: (props) => ({
      context: {
        headers: {
          'X-Item-Region': props.params.shop ? EVERYWHERE_CODE : undefined,
        },
      },
      ssr: true,
      variables: {
        shop: props.params.shop,
        loggedOut: props.user.loggedOut,
      },
    }),
  },
);

export default withUserContext<Props>(connect(ShopContainer));

